import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import { useQuery } from '@apollo/react-hooks';
import { queries } from '../../graphql/graphql';
import _s from './index.less';

const AvailableArea = ({ available = {}, setAvailable, disabled = false }) => {
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCounties, setAvailableCounties] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  const statesResponse = useQuery(queries.DIRECTORY, { variables: { slug: 'us-states' } });
  const countiesResponse = useQuery(queries.DIRECTORIES_VALUES_BY_IDS, {
    variables: { ids: availableStates }
  });
  const citiesResponse = useQuery(queries.DIRECTORIES_VALUES_BY_IDS, {
    variables: { ids: availableCounties }
  });

  const validateStates = useCallback(() => {
    if (!statesResponse.loading && statesResponse.data && statesResponse.data.directory) {
      const validStateIds = statesResponse.data.directory.values.map(
        item => item.directory || item.id
      );
      const filteredStates = availableStates.filter(stateId => validStateIds.includes(stateId));

      if (filteredStates.length !== availableStates.length) {
        setAvailableStates(filteredStates);
        setAvailable({ ...available, states: filteredStates, counties: [], cities: [] });
      }
    }
  }, [statesResponse, availableStates, available, setAvailable]);

  const validateCounties = useCallback(() => {
    if (
      !countiesResponse.loading &&
      countiesResponse.data &&
      countiesResponse.data.directoriesValuesByIds
    ) {
      const validCountyIds = countiesResponse.data.directoriesValuesByIds.map(
        item => item.directory || item.id
      );
      const filteredCounties = availableCounties.filter(countyId =>
        validCountyIds.includes(countyId)
      );

      if (filteredCounties.length !== availableCounties.length) {
        setAvailableCounties(filteredCounties);
        setAvailable({ ...available, counties: filteredCounties });
      }
    }
  }, [countiesResponse, availableCounties, available, setAvailable]);

  const validateCities = useCallback(() => {
    if (
      !citiesResponse.loading &&
      citiesResponse.data &&
      citiesResponse.data.directoriesValuesByIds
    ) {
      const validCityIds = citiesResponse.data.directoriesValuesByIds.map(
        item => item.directory || item.id
      );
      const filteredCities = availableCities.filter(cityId => validCityIds.includes(cityId));

      if (filteredCities.length !== availableCities.length) {
        setAvailableCities(filteredCities);
        setAvailable({ ...available, cities: filteredCities });
      }
    }
  }, [citiesResponse, availableCities, available, setAvailable]);

  useEffect(() => {
    validateStates();
    validateCounties();
    validateCities();
  }, [validateStates, validateCounties, validateCities]);

  useEffect(() => {
    if (available) {
      setAvailableStates(available.states || []);
      setAvailableCounties(available.counties || []);
      setAvailableCities(available.cities || []);
    }
  }, [available]);

  const getOptions = data => {
    return data.map(item => ({
      label: item.name,
      value: item.directory || item.id
    }));
  };

  return statesResponse.loading || countiesResponse.loading || citiesResponse.loading ? (
    <Spin />
  ) : (
    <>
      <div className={_s.answerBlock}>
        <Select
          disabled={disabled}
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Available States"
          onChange={value => {
            setAvailableStates(value);
            setAvailable({ ...available, states: value });
          }}
          options={getOptions(statesResponse.data.directory.values)}
          value={availableStates}
        />
      </div>
      {availableStates.length > 0 && (
        <div className={_s.answerBlock}>
          <Select
            disabled={disabled}
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Available Counties"
            onChange={value => {
              setAvailableCounties(value);
              setAvailable({ ...available, counties: value });
            }}
            options={getOptions(countiesResponse.data.directoriesValuesByIds)}
            value={availableCounties}
          />
        </div>
      )}
      {availableCounties.length > 0 && (
        <div className={_s.answerBlock}>
          <Select
            disabled={disabled}
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Available Cities"
            onChange={value => {
              setAvailableCities(value);
              setAvailable({ ...available, cities: value });
            }}
            options={getOptions(citiesResponse.data.directoriesValuesByIds)}
            value={availableCities}
          />
        </div>
      )}
    </>
  );
};

AvailableArea.propTypes = {
  available: PropTypes.shape({
    states: PropTypes.arrayOf(PropTypes.string),
    counties: PropTypes.arrayOf(PropTypes.string),
    cities: PropTypes.arrayOf(PropTypes.string)
  }),
  setAvailable: PropTypes.func.isRequired
};

export default AvailableArea;
